import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { leadGenAPI, linkedinAPI, googleMapsAPI } from "../services/api";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  Grid,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import RefreshIcon from "@mui/icons-material/Refresh";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// LinkedIn location IDs mapping
const LINKEDIN_LOCATION_IDS = {
  Mexico: 103323778,
  Argentina: 100446943,
  Bolivia: 104379274,
  Brazil: 106057199,
  Colombia: 100876405,
  Ecuador: 106373116,
  Guyana: 105836293,
  Paraguay: 104065273,
  Peru: 102927786,
  Suriname: 105530931,
  Uruguay: 100867946,
  Venezuela: 101490751,
  Canada: 101174742,
  "United States": 103644278,
  Belize: 105912732,
  "Costa Rica": 101739942,
  "El Salvador": 106522560,
  Guatemala: 100877388,
  Honduras: 101937718,
  Nicaragua: 105517145,
  Panama: 100808673,
  Cuba: 106429766,
  Haiti: 100993490,
  "Dominican Republic": 105057336,
};

// Get country name from location ID (for reverse lookup)
const getCountryNameFromId = (locationId) => {
  const entry = Object.entries(LINKEDIN_LOCATION_IDS).find(
    ([_, id]) => id.toString() === locationId.toString()
  );
  return entry ? entry[0] : "";
};

// Get country names from location IDs string (e.g., "[103644278]" or array)
const getCountryNamesFromLocationIds = (locationIds) => {
  if (!locationIds) return "";

  let idsArray = [];
  if (typeof locationIds === "string") {
    try {
      // Try to parse as JSON array string like "[103644278]"
      idsArray = JSON.parse(locationIds);
    } catch (e) {
      // If not JSON, try splitting by comma
      idsArray = locationIds
        .split(",")
        .map((id) => id.trim().replace(/[\[\]]/g, ""));
    }
  } else if (Array.isArray(locationIds)) {
    idsArray = locationIds;
  }

  const countryNames = idsArray
    .map((id) => {
      const entry = Object.entries(LINKEDIN_LOCATION_IDS).find(
        ([_, locationId]) => locationId.toString() === id.toString()
      );
      return entry ? entry[0] : null;
    })
    .filter((name) => name !== null);

  return countryNames.join(", ") || "";
};

const CompanyResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");
  const source = searchParams.get("source") || "googlemaps"; // googlemaps or linkedin
  const fromPage = searchParams.get("from") || "candidate-sourcing"; // candidate-sourcing or lead-history

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [hasLoadedDecisionMakers, setHasLoadedDecisionMakers] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [availableTitles, setAvailableTitles] = useState([]);
  const [isLoadingDecisionMakers, setIsLoadingDecisionMakers] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedDecisionMaker, setSelectedDecisionMaker] = useState(null);
  const [selectedDecisionMakers, setSelectedDecisionMakers] = useState([]);

  // Hardcoded roles for decision makers
  const availableRoles = [
    "owner",
    "founder",
    "c_suite",
    "partner",
    "vp",
    "head",
    "director",
    "manager",
    "senior",
    "entry",
    "intern",
  ];
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [noOfDecisionMakers, setNoOfDecisionMakers] = useState(1);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [aiMessage, setAiMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);

  // LinkedIn status checking states
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [searchStatus, setSearchStatus] = useState(null);
  const [showStatusScreen, setShowStatusScreen] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);

  // Determine if this is Google Maps or LinkedIn based on source
  const isGoogleMaps = source === "googlemaps";

  // Load companies on mount
  useEffect(() => {
    if (jobId) {
      if (source === "linkedin") {
        // For LinkedIn, show status screen and automatically check status
        setShowStatusScreen(true);
        // Wait a moment then check status automatically
        setTimeout(() => {
          checkSearchStatus();
        }, 500);
      } else {
        // For Google Maps, load companies directly
        loadCompanies();
      }
    } else {
      toast.error("Job ID is required");
      navigate("/candidate-sourcing");
    }

    // Cleanup interval on unmount
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
    };
  }, [jobId, source]);

  // Check LinkedIn search status
  const checkSearchStatus = async () => {
    if (!jobId) return;

    setIsCheckingStatus(true);

    try {
      const statusResponse = await linkedinAPI.getStatus(jobId);

      if (statusResponse.success && statusResponse.data) {
        const statusData = statusResponse.data;
        setSearchStatus(statusData);

        if (
          statusData.status === "completed" ||
          statusData.status === "complete"
        ) {
          // Status is completed
          setIsCheckingStatus(false);
          setShowStatusScreen(false);

          // Call Apollo people search API instead of loading companies
          await callApolloPeopleSearch();
        } else if (statusData.status === "failed") {
          // Search failed
          setIsCheckingStatus(false);
          toast.error(
            statusData.error_message ||
              statusData.progress_message ||
              "Search failed"
          );
        } else {
          // Still processing or pending
          setIsCheckingStatus(false);
        }
      } else {
        setIsCheckingStatus(false);
      }
    } catch (error) {
      console.error("Error checking search status:", error);
      setIsCheckingStatus(false);
      toast.error("Failed to check status. Please try again.");
    }
  };

  // Call Apollo people search for LinkedIn when status is complete
  const callApolloPeopleSearch = async () => {
    if (!jobId) return;

    setIsLoading(true);
    try {
      // Get search_name from localStorage search history
      let searchName = "";
      const existingHistory = localStorage.getItem("lead_search_history");
      if (existingHistory) {
        try {
          const history = JSON.parse(existingHistory);
          const searchItem = history.find(
            (item) => item.linkedinJobId === jobId
          );
          if (searchItem) {
            searchName = searchItem.batchName || "";
          }
        } catch (error) {
          console.error("Error parsing search history:", error);
        }
      }

      // If search_name not found in history, use a default or get from status
      if (!searchName && searchStatus?.search_name) {
        searchName = searchStatus.search_name;
      }

      // If still no search_name, use jobId as fallback
      if (!searchName) {
        searchName = `search_${jobId}`;
      }

      console.log("Calling Apollo people search with:", {
        jobId,
        searchName,
      });

      // Call Apollo people search API
      const response = await linkedinAPI.apolloPeopleSearch(jobId, searchName);

      console.log("Apollo people search response:", response);

      if (response.success) {
        // Map businesses (note: API has typo "buisinesses")
        const businesses = response.buisinesses || response.businesses || [];
        const mappedCompanies = businesses.map((company, index) => ({
          id:
            company.id ||
            company.company_id ||
            `linkedin_company_${jobId}_${index}`,
          company_apollo_id:
            company.company_id ||
            company.id ||
            `linkedin_company_${jobId}_${index}`,
          place_id:
            company.company_id ||
            company.id ||
            `linkedin_company_${jobId}_${index}`,
          name: company.company_name || company.name || "Unknown Company",
          description: company.description || company.industry || "",
          industry: company.industry || company.industry_names || "",
          tagline: company.description || "",
          website:
            company.website_url ||
            company.regular_company_url ||
            company.company_url ||
            "",
          website_url:
            company.website_url ||
            company.regular_company_url ||
            company.company_url ||
            "",
          phone: company.phone || company.phone_number || "",
          phone_number: company.phone || company.phone_number || "",
          address:
            company.address ||
            company.location ||
            getCountryNamesFromLocationIds(company.location_ids) ||
            "",
          full_address:
            company.address ||
            company.location ||
            getCountryNamesFromLocationIds(company.location_ids) ||
            "",
          rating:
            company.industry || company.industry_names || company.rating || 0,
          review_count: company.review_count || 0,
          employees_count: company.employees_count || "",
          employee_count_range: company.employee_count_range || "",
          logo_url: company.logo_url || "",
          company_url: company.company_url || "",
          regular_company_url:
            company.regular_company_url || company.company_url || "",
          is_hiring: company.is_hiring || false,
          query: company.query || "",
          timestamp: company.timestamp || "",
          search_account_profile_id: company.search_account_profile_id || "",
          search_account_profile_name:
            company.search_account_profile_name || "",
          job_id: company.job_id || jobId,
          search_name: company.search_name || searchName || "",
          location_ids: company.location_ids || "",
          created_at: company.created_at || "",
          role: company.role || "",
          ...company,
        }));

        // Map people from search_response.people
        const people =
          response.search_response?.people || response.enriched_data || [];
        const mappedDecisionMakers = people.map((person) => {
          const fullName =
            person.name ||
            `${person.first_name || ""} ${
              person.last_name || person.last_name_obfuscated || ""
            }`.trim() ||
            "Unknown";

          return {
            id: person.id || `dm_${fullName}_${Date.now()}`,
            name: fullName,
            full_name: fullName,
            first_name: person.first_name || "",
            last_name: person.last_name || person.last_name_obfuscated || "",
            title: person.title || "",
            email: person.email || "",
            linkedin_url: person.linkedin_url || "",
            photo_url: person.photo_url || "",
            headline: person.headline || person.title || "",
            organization: person.organization || {},
            formatted_address: person.formatted_address || "",
            city: person.city || "",
            state: person.state || "",
            country: person.country || "",
            phone_number: person.phone_number || "",
            has_email: person.has_email || false,
            has_direct_phone: person.has_direct_phone || "",
            employment_history: person.employment_history || [],
            seniority: person.seniority || "",
            departments: person.departments || [],
            subdepartments: person.subdepartments || [],
            functions: person.functions || [],
            last_refreshed_at: person.last_refreshed_at || "",
            ...person,
          };
        });

        // Set companies and decision makers
        setCompanies(mappedCompanies);
        if (mappedDecisionMakers.length > 0) {
          setDecisionMakers(mappedDecisionMakers);
          setHasLoadedDecisionMakers(true);

          // Extract available titles from decision makers
          const titles = [
            ...new Set(
              mappedDecisionMakers.map((dm) => dm.title).filter(Boolean)
            ),
          ];
          setAvailableTitles(titles);
        }

        // Hide status screen and show results
        setShowStatusScreen(false);

        toast.success(
          `Found ${mappedCompanies.length} companies and ${mappedDecisionMakers.length} decision makers`
        );
      } else {
        const errorMsg =
          response.message || response.detail || "Apollo people search failed";
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      console.error("Error calling Apollo people search:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        "Failed to call Apollo people search";
      toast.error(errorMsg, { duration: 6000 });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompanies = async () => {
    if (!jobId) return;

    setIsLoading(true);
    try {
      let mappedCompanies = [];

      if (source === "linkedin") {
        // Load LinkedIn companies
        const response = await linkedinAPI.getCompanies(jobId);
        const companiesList = response.companies || [];

        // Map LinkedIn companies to the expected format
        mappedCompanies = companiesList.map((company, index) => ({
          id:
            company.company_id ||
            company.id ||
            `linkedin_company_${jobId}_${index}`,
          company_apollo_id:
            company.company_id ||
            company.id ||
            `linkedin_company_${jobId}_${index}`,
          place_id:
            company.company_id ||
            company.id ||
            `linkedin_company_${jobId}_${index}`,
          name: company.company_name || company.name || "Unknown Company",
          description: company.description || company.industry || "",
          industry: company.industry || company.description || "",
          tagline: company.description || "",
          website: company.regular_company_url || company.company_url || "",
          website_url: company.regular_company_url || company.company_url || "",
          phone: company.phone || company.phone_number || "",
          phone_number: company.phone || company.phone_number || "",
          address:
            company.address ||
            company.location ||
            getCountryNamesFromLocationIds(company.location_ids) ||
            "",
          full_address:
            company.address ||
            company.location ||
            getCountryNamesFromLocationIds(company.location_ids) ||
            "",
          rating:
            company.industry || company.industry_names || company.rating || 0,
          review_count: company.review_count || 0,
          employees_count: company.employees_count || "",
          employee_count_range: company.employee_count_range || "",
          logo_url: company.logo_url || "",
          company_url: company.company_url || "",
          regular_company_url: company.regular_company_url || "",
          is_hiring: company.is_hiring || false,
          query: company.query || "",
          timestamp: company.timestamp || "",
          search_account_profile_id: company.search_account_profile_id || "",
          search_account_profile_name:
            company.search_account_profile_name || "",
          job_id: company.job_id || jobId,
          search_name: company.search_name || "",
          location_ids: company.location_ids || "",
          created_at: company.created_at || "",
          ...company,
        }));
      } else {
        // Load Google Maps companies from search response stored in localStorage
        const storedResponse = localStorage.getItem(
          `google_maps_search_${jobId}`
        );

        if (storedResponse) {
          try {
            const searchResponse = JSON.parse(storedResponse);
            const businesses = searchResponse.businesses || [];
            const enrichedData = searchResponse.enriched_data || [];

            // Map businesses to companies
            mappedCompanies = businesses.map((business, index) => ({
              id:
                business.place_id ||
                business.job_id ||
                `company_${jobId}_${index}`,
              company_apollo_id:
                business.place_id ||
                business.job_id ||
                `company_${jobId}_${index}`,
              place_id:
                business.place_id ||
                business.job_id ||
                `company_${jobId}_${index}`,
              name: business.name || "Unknown Company",
              description: business.business_type || "",
              industry: business.business_type || "",
              tagline: business.subtypes || "",
              website: business.website || "",
              website_url: business.website || "",
              phone: business.phone_number || "",
              phone_number: business.phone_number || "",
              address: business.full_address || "",
              full_address: business.full_address || "",
              rating: business.rating || 0,
              review_count: business.review_count || 0,
              business_status: business.business_status || "",
              business_type: business.business_type || "",
              subtypes: business.subtypes || "",
              timezone: business.timezone || "",
              owner_name: business.owner_name || "",
              job_id: business.job_id || jobId,
              ...business,
            }));

            // Set decision makers from enriched_data
            if (enrichedData.length > 0) {
              const mappedDecisionMakers = enrichedData.map((person) => {
                const fullName =
                  person.name ||
                  `${person.first_name || ""} ${
                    person.last_name || ""
                  }`.trim() ||
                  "Unknown";
                return {
                  id: person.id || `dm_${fullName}_${Date.now()}`,
                  name: fullName,
                  full_name: fullName, // For backward compatibility
                  first_name: person.first_name || "",
                  last_name: person.last_name || "",
                  title: person.title || "",
                  email: person.email || "",
                  linkedin_url: person.linkedin_url || "",
                  photo_url: person.photo_url || "",
                  headline: person.headline || "",
                  organization: person.organization || {},
                  formatted_address: person.formatted_address || "",
                  city: person.city || "",
                  state: person.state || "",
                  country: person.country || "",
                  employment_history: person.employment_history || [],
                  seniority: person.seniority || "",
                  departments: person.departments || [],
                  subdepartments: person.subdepartments || [],
                  functions: person.functions || [],
                  ...person,
                };
              });

              setDecisionMakers(mappedDecisionMakers);
              setHasLoadedDecisionMakers(true);

              // Extract available titles from decision makers
              const titles = [
                ...new Set(
                  mappedDecisionMakers.map((dm) => dm.title).filter(Boolean)
                ),
              ];
              setAvailableTitles(titles);
            }
          } catch (error) {
            console.error("Error parsing stored search response:", error);
            toast.error("Failed to load search results");
          }
        } else {
          // Fallback to API call if stored response not found
          const response = await googleMapsAPI.getJobDetails(jobId);
          const businesses = response.businesses || [];

          if (businesses.length === 0) {
            toast.info("No companies found for this job");
            setCompanies([]);
            setIsLoading(false);
            return;
          }

          mappedCompanies = businesses.map((business, index) => ({
            id: business.place_id || business.id || `company_${jobId}_${index}`,
            company_apollo_id:
              business.place_id || business.id || `company_${jobId}_${index}`,
            place_id:
              business.place_id || business.id || `company_${jobId}_${index}`,
            name: business.name || "Unknown Company",
            description: business.business_type || "",
            industry: business.business_type || "",
            tagline: business.subtypes || "",
            website: business.website || "",
            website_url: business.website || "",
            phone: business.phone_number || "",
            phone_number: business.phone_number || "",
            address: business.full_address || "",
            full_address: business.full_address || "",
            rating: business.rating || 0,
            review_count: business.review_count || 0,
            business_status: business.business_status || "",
            business_type: business.business_type || "",
            subtypes: business.subtypes || "",
            timezone: business.timezone || "",
            owner_name: business.owner_name || "",
            job_id: business.job_id || jobId,
            ...business,
          }));
        }
      }

      setCompanies(mappedCompanies);

      if (mappedCompanies.length === 0) {
        toast.info("No companies found for the selected criteria");
      } else {
        toast.success(`Loaded ${mappedCompanies.length} companies`);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
      toast.error("Failed to load companies");
      setCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSearch = () => {
    // Navigate back to the page we came from
    if (fromPage === "lead-history") {
      navigate("/lead-history");
    } else {
      navigate("/candidate-sourcing");
    }
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleFindDecisionMakers = async () => {
    if (companies.length === 0) {
      toast.error("No companies available");
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    if (
      !noOfDecisionMakers ||
      noOfDecisionMakers < 1 ||
      noOfDecisionMakers > 20
    ) {
      toast.error("Please enter a valid number of decision makers (1-20)");
      return;
    }

    setIsLoadingDecisionMakers(true);
    try {
      // Use all companies instead of selected ones
      const companiesNameList = companies.map(
        (company) => company.name || "Unknown Company"
      );

      const websiteUrls = companies
        .map((company) => {
          const url =
            company.website ||
            company.website_url ||
            company.regular_company_url ||
            company.company_url;
          if (
            url &&
            (url.startsWith("http://") || url.startsWith("https://"))
          ) {
            return url;
          } else if (url) {
            return `https://${url}`;
          }
          return null;
        })
        .filter((url) => url !== null);

      const apolloPayload = {
        google_maps_agent_call: isGoogleMaps,
        no_of_decision_makers: parseInt(noOfDecisionMakers),
        companies_name_list: companiesNameList,
        list_of_roles: selectedRoles,
        website_urls: websiteUrls,
      };

      const response = await leadGenAPI.searchPeople(apolloPayload);

      let decisionMakersList = [];
      if (response.enriched_data && Array.isArray(response.enriched_data)) {
        decisionMakersList = response.enriched_data;
      } else if (
        response.search_response?.people &&
        Array.isArray(response.search_response.people)
      ) {
        decisionMakersList = response.search_response.people;
      } else if (Array.isArray(response)) {
        decisionMakersList = response;
      } else if (response.data && Array.isArray(response.data)) {
        decisionMakersList = response.data;
      } else if (
        response.decision_makers &&
        Array.isArray(response.decision_makers)
      ) {
        decisionMakersList = response.decision_makers;
      } else if (response.people && Array.isArray(response.people)) {
        decisionMakersList = response.people;
      }

      const mappedDecisionMakers = decisionMakersList.map((dm) => {
        const organizationName =
          dm.organization?.name || dm.organization_name || "";
        const organizationId = dm.organization?.id || dm.organization_id || "";

        const fullName =
          dm.name ||
          `${dm.first_name || ""} ${dm.last_name || ""}`.trim() ||
          "Unknown";

        let location = dm.formatted_address || "";
        if (!location && (dm.city || dm.state || dm.country)) {
          const locationParts = [dm.city, dm.state, dm.country].filter(Boolean);
          location = locationParts.join(", ");
        }

        return {
          person_id: dm.id || dm.person_id,
          first_name: dm.first_name || "",
          last_name: dm.last_name || "",
          full_name: fullName,
          title: dm.title || dm.headline || "",
          email: dm.email || "",
          linkedin_url: dm.linkedin_url || "",
          photo_url: dm.photo_url || "",
          organization_id: organizationId,
          organization_name: organizationName,
          domain: dm.organization?.primary_domain || "",
          phone_number: dm.phone || dm.phone_number || "",
          location: location,
          city: dm.city || "",
          state: dm.state || "",
          country: dm.country || "",
          postal_code: dm.postal_code || "",
          headline: dm.headline || "",
          email_status: dm.email_status || "",
          seniority: dm.seniority || "",
          ...dm,
        };
      });

      setDecisionMakers(mappedDecisionMakers);
      setHasLoadedDecisionMakers(true);

      const titles = [
        ...new Set(mappedDecisionMakers.map((dm) => dm.title).filter(Boolean)),
      ];
      setAvailableTitles(titles);

      toast.success(`Found ${mappedDecisionMakers.length} decision makers`);
    } catch (error) {
      console.error("Error fetching decision makers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load decision makers";
      toast.error(errorMessage);
      setDecisionMakers([]);
    } finally {
      setIsLoadingDecisionMakers(false);
    }
  };

  const handleCompanyNameClick = (e, company) => {
    e.stopPropagation();
    if (company.website || company.website_url) {
      const url = company.website || company.website_url;
      window.open(url.startsWith("http") ? url : `https://${url}`, "_blank");
    }
  };

  const filteredDecisionMakers =
    decisionMakers.length > 0 && selectedTitles.length > 0
      ? decisionMakers.filter((dm) => {
          return selectedTitles.includes(dm.title);
        })
      : decisionMakers;

  const handleTitleToggle = (title) => {
    setSelectedTitles((prev) => {
      if (prev.includes(title)) {
        return prev.filter((t) => t !== title);
      } else {
        return [...prev, title];
      }
    });
  };

  const handleDecisionMakerToggle = (dm) => {
    setSelectedDecisionMakers((prev) => {
      const dmId = dm.person_id || dm.id;
      const isSelected = prev.some((d) => (d.person_id || d.id) === dmId);
      if (isSelected) {
        return prev.filter((d) => (d.person_id || d.id) !== dmId);
      } else {
        return [...prev, dm];
      }
    });
  };

  const isDecisionMakerSelected = (dm) => {
    const dmId = dm.person_id || dm.id;
    return selectedDecisionMakers.some((d) => (d.person_id || d.id) === dmId);
  };

  const handleGenerateMessage = async () => {
    if (!userPrompt.trim()) {
      toast.error("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
      toast.success("Message generated successfully!");
    } catch (error) {
      console.error("Error generating message:", error);
      toast.error("Failed to generate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateMessage = async () => {
    if (!userPrompt.trim()) {
      toast.error("Please enter a message prompt");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await linkedinAPI.generateMessage(userPrompt);
      setAiMessage(response.message || response.text || "");
      toast.success("Message regenerated successfully!");
    } catch (error) {
      console.error("Error regenerating message:", error);
      toast.error("Failed to regenerate message. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessages = async () => {
    if (!aiMessage.trim()) {
      toast.error("Please generate a message first");
      return;
    }

    if (selectedDecisionMakers.length === 0) {
      toast.error("No decision makers selected to send messages to");
      return;
    }

    setIsSending(true);
    const decisionMakersWithLinkedIn = selectedDecisionMakers.filter(
      (dm) => dm.linkedin_url
    );

    if (decisionMakersWithLinkedIn.length === 0) {
      toast.error("No decision makers with LinkedIn profiles found");
      setIsSending(false);
      return;
    }

    try {
      const linkedInUrls = decisionMakersWithLinkedIn.map(
        (dm) => dm.linkedin_url
      );

      await linkedinAPI.sendMessage(linkedInUrls, aiMessage);

      const successCount = linkedInUrls.length;

      toast.success(`Successfully sent ${successCount} message(s)!`);
      setMessageModalOpen(false);
      setUserPrompt("");
      setAiMessage("");
      setSelectedDecisionMakers([]);
    } catch (error) {
      console.error("Error sending messages:", error);
      toast.error("Failed to send messages. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleShortlistDecisionMakers = async () => {
    if (selectedDecisionMakers.length === 0) {
      toast.error("No decision makers selected to shortlist");
      return;
    }

    setIsShortlisting(true);
    try {
      // Extract Apollo IDs from selected decision makers
      const apolloIds = selectedDecisionMakers
        .map((dm) => dm.person_id || dm.id)
        .filter((id) => id); // Remove any null/undefined values

      if (apolloIds.length === 0) {
        toast.error("No valid Apollo IDs found in selected decision makers");
        setIsShortlisting(false);
        return;
      }

      await leadGenAPI.shortlistDecisionMakers(apolloIds);

      toast.success(
        `Successfully shortlisted ${apolloIds.length} decision maker(s)!`
      );

      // Clear selection after successful shortlisting
      setSelectedDecisionMakers([]);
    } catch (error) {
      console.error("Error shortlisting decision makers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to shortlist decision makers. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsShortlisting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Scrollable Container */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
          paddingBottom: {
            xs: "calc(60px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(40px + env(safe-area-inset-bottom, 0px))",
            md: "0px",
          },
          WebkitOverflowScrolling: "touch",
          marginBottom: { xs: "0px", md: "0px" },
        }}
      >
        {/* Title Section */}
        <Box
          sx={{ padding: { xs: "16px 16px 0 16px", sm: "24px 24px 0 24px" } }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "20px", sm: "28px", md: "32px" },
            }}
          >
            Find Companies & Decision Makers
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: "13px", sm: "14px", md: "16px" } }}
          >
            Search for companies and extract decision makers for your outreach
            campaign
          </Typography>
        </Box>

        {/* LinkedIn Status Checking UI */}
        {showStatusScreen && source === "linkedin" && (
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
            }}
          >
            <Card
              sx={{
                padding: "40px",
                maxWidth: "600px",
                width: "100%",
                textAlign: "center",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              {isCheckingStatus ? (
                <CircularProgress size={60} sx={{ color: "#3B82F6", mb: 3 }} />
              ) : (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    backgroundColor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                  }}
                >
                  <LinkedInIcon sx={{ fontSize: 32, color: "#3B82F6" }} />
                </Box>
              )}
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mb: 2, color: "#1F2937" }}
              >
                {isCheckingStatus
                  ? "Checking Status..."
                  : searchStatus?.status === "completed"
                  ? "Search Completed!"
                  : searchStatus?.status === "failed"
                  ? "Search Failed"
                  : "Search in Progress"}
              </Typography>
              <Typography variant="body1" sx={{ color: "#6B7280", mb: 3 }}>
                {isCheckingStatus
                  ? "Please wait while we check the search status..."
                  : searchStatus?.status === "completed"
                  ? "Your search has been completed successfully. Processing results..."
                  : searchStatus?.status === "failed"
                  ? searchStatus?.error_message ||
                    searchStatus?.progress_message ||
                    "Search failed. Please try again."
                  : "We are currently searching LinkedIn for companies matching your criteria. Kindly refresh after 1-2 minutes to check the status."}
              </Typography>

              {/* Refresh Button */}
              <Button
                variant="contained"
                onClick={checkSearchStatus}
                disabled={isCheckingStatus}
                sx={{
                  mt: 2,
                  backgroundColor: "#3B82F6",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                  minWidth: "150px",
                }}
                startIcon={
                  isCheckingStatus ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                {isCheckingStatus ? "Checking..." : "Refresh Status"}
              </Button>
            </Card>
          </Box>
        )}

        {/* Two Panel Layout */}
        {!showStatusScreen && (
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Grid container spacing={3}>
              {/* Left Panel: Companies Found */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Companies Found ({companies.length})
                  </Typography>
                  <Link
                    component="button"
                    onClick={handleBackToSearch}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "primary.main",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: "16px" }} />
                    <Typography variant="body2">Back to Search</Typography>
                  </Link>
                </Box>

                {/* Company Cards */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  {isLoading ? (
                    <Typography
                      sx={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6B7280",
                      }}
                    >
                      Loading companies...
                    </Typography>
                  ) : companies.length > 0 ? (
                    companies.map((company, index) => (
                      <Card
                        key={company.company_apollo_id || index}
                        sx={{
                          backgroundColor: "#FFFFFF",
                          borderRadius: "12px",
                          padding: "12px 16px",
                          border: "1px solid #E5E7EB",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                position: "absolute",
                                top: "12px",
                                right: "16px",
                              }}
                            >
                              {!(source === "linkedin" && company.logo_url) && (
                                <CorporateFareIcon
                                  sx={{
                                    color: "#3B82F6",
                                    fontSize: "20px",
                                  }}
                                />
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                mb: 1.5,
                              }}
                            >
                              {source === "linkedin" && company.logo_url && (
                                <Box
                                  component="img"
                                  src={company.logo_url}
                                  alt={company.name}
                                  sx={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    border: "1px solid #E5E7EB",
                                    flexShrink: 0,
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <Typography
                                variant="h6"
                                onClick={(e) =>
                                  handleCompanyNameClick(e, company)
                                }
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "18px",
                                  paddingRight: "40px",
                                  color: "#101828",
                                  cursor:
                                    company.website || company.website_url
                                      ? "pointer"
                                      : "default",
                                  "&:hover": {
                                    color:
                                      company.website || company.website_url
                                        ? "#3B82F6"
                                        : "#101828",
                                    textDecoration:
                                      company.website || company.website_url
                                        ? "underline"
                                        : "none",
                                  },
                                  transition: "color 0.2s",
                                  flex: 1,
                                }}
                              >
                                {company.name}
                              </Typography>
                            </Box>

                            {source === "linkedin" && company.industry && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                Industry: {company.industry}
                              </Typography>
                            )}

                            {source === "linkedin" && company.description && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "13px",
                                  lineHeight: "18px",
                                  maxHeight: "54px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {company.description}
                              </Typography>
                            )}

                            {company.business_type && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                Type: {company.business_type}
                              </Typography>
                            )}

                            {source === "linkedin" &&
                              company.employee_count_range && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#6B7280",
                                    mb: 1,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                  }}
                                >
                                  Employees: {company.employee_count_range}
                                  {company.employees_count &&
                                    ` (${company.employees_count})`}
                                </Typography>
                              )}

                            {source === "linkedin" &&
                              company.is_hiring !== undefined && (
                                <Chip
                                  label={
                                    company.is_hiring
                                      ? "Currently Hiring"
                                      : "Not Hiring"
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: company.is_hiring
                                      ? "#10B981"
                                      : "#6B7280",
                                    color: "#FFFFFF",
                                    fontSize: "11px",
                                    height: "22px",
                                    mb: 1,
                                  }}
                                />
                              )}

                            {company.phone_number && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 1,
                                  fontSize: "14px",
                                }}
                              >
                                Phone: {company.phone_number}
                              </Typography>
                            )}

                            {company.full_address && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  mb: 0,
                                  fontSize: "14px",
                                  lineHeight: "20px",
                                }}
                              >
                                Address: {company.full_address}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6B7280",
                      }}
                    >
                      No companies found
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Right Panel: Decision Makers */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2.5,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Decision Makers
                  </Typography>
                  {hasLoadedDecisionMakers &&
                    selectedDecisionMakers.length > 0 && (
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                          variant="contained"
                          startIcon={<BookmarkIcon />}
                          onClick={handleShortlistDecisionMakers}
                          disabled={isShortlisting}
                          sx={{
                            backgroundColor: "#10B981",
                            "&:hover": {
                              backgroundColor: "#059669",
                            },
                            textTransform: "none",
                            borderRadius: "8px",
                          }}
                        >
                          {isShortlisting
                            ? "Shortlisting..."
                            : `Shortlisted (${selectedDecisionMakers.length})`}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<LinkedInIcon />}
                          onClick={() => setMessageModalOpen(true)}
                          sx={{
                            backgroundColor: "#3B82F6",
                            "&:hover": {
                              backgroundColor: "#2563EB",
                            },
                            textTransform: "none",
                            borderRadius: "8px",
                          }}
                        >
                          Send Message ({selectedDecisionMakers.length})
                        </Button>
                      </Box>
                    )}
                </Box>

                {/* Role Selection - Only for LinkedIn */}

                {/* Title Filter Chips */}
                {availableTitles.length > 0 && (
                  <Box sx={{ marginBottom: "20px" }}>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Filter by Title
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      {availableTitles.map((title) => (
                        <Chip
                          key={title}
                          label={title}
                          onClick={() => handleTitleToggle(title)}
                          clickable
                          sx={{
                            backgroundColor: selectedTitles.includes(title)
                              ? "primary.main"
                              : "#FFFFFF",
                            color: selectedTitles.includes(title)
                              ? "#FFFFFF"
                              : "text.primary",
                            border: "1px solid #D1D5DB",
                            height: "32px",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: selectedTitles.includes(title)
                                ? "primary.dark"
                                : "#F9FAFB",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Loading State */}
                {isLoadingDecisionMakers && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "40px",
                      minHeight: "300px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center" }}
                    >
                      Loading decision makers...
                    </Typography>
                  </Box>
                )}

                {/* Decision Maker Cards */}
                {!isLoadingDecisionMakers && hasLoadedDecisionMakers && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginBottom: "20px",
                      maxHeight: "800px",
                      overflowY: "auto",
                    }}
                  >
                    {filteredDecisionMakers.length > 0 ? (
                      filteredDecisionMakers.map((dm, index) => (
                        <Card
                          key={dm.person_id || index}
                          sx={{
                            backgroundColor: isDecisionMakerSelected(dm)
                              ? "#EFF6FF"
                              : "#FFFFFF",
                            borderRadius: "12px",
                            padding: "20px",
                            minHeight: "100px",
                            boxShadow: isDecisionMakerSelected(dm)
                              ? "0 2px 4px rgba(59, 130, 246, 0.2)"
                              : "0 1px 3px rgba(0, 0, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            border: isDecisionMakerSelected(dm)
                              ? "2px solid #3B82F6"
                              : "1px solid transparent",
                            "&:hover": {
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              gap: "16px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDecisionMakerToggle(dm);
                            }}
                          >
                            <Checkbox
                              checked={isDecisionMakerSelected(dm)}
                              onChange={() => handleDecisionMakerToggle(dm)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: "#3B82F6",
                                "&.Mui-checked": {
                                  color: "#3B82F6",
                                },
                              }}
                            />
                            {dm.photo_url ? (
                              <Box
                                component="img"
                                src={dm.photo_url}
                                alt={dm.name || dm.full_name || "Profile"}
                                sx={{
                                  width: "56px",
                                  height: "56px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  width: "56px",
                                  height: "56px",
                                  bgcolor: "#D1D5DB",
                                  color: "#6B7280",
                                  fontSize: "20px",
                                  fontWeight: 600,
                                }}
                              >
                                {(
                                  dm.name ||
                                  dm.full_name ||
                                  `${dm.first_name || ""} ${
                                    dm.last_name || ""
                                  }`.trim()
                                )
                                  ?.charAt(0)
                                  ?.toUpperCase() || "?"}
                              </Avatar>
                            )}
                            <Box
                              sx={{ flex: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDecisionMaker(dm);
                                setProfileModalOpen(true);
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,

                                  fontSize: "16px",
                                }}
                              >
                                {dm.name ||
                                  dm.full_name ||
                                  `${dm.first_name || ""} ${
                                    dm.last_name || ""
                                  }`.trim() ||
                                  "Unknown"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "primary.main",

                                  fontSize: "14px",
                                }}
                              >
                                {dm.title}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <EmailIcon
                                  sx={{
                                    fontSize: "16px",
                                    color: "#6B7280",
                                  }}
                                />
                                <Typography variant="body2">
                                  {dm.email || "No email"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            {dm.linkedin_url && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(dm.linkedin_url, "_blank");
                                }}
                                sx={{ color: "#0A66C2" }}
                              >
                                <LinkedInIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Card>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", p: 2.5 }}
                      >
                        {selectedRoles.length === 0
                          ? "Select roles to find decision makers"
                          : selectedTitles.length === 0
                          ? "No titles selected. Please select titles to filter decision makers."
                          : "No decision makers found for the selected titles."}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Empty State */}
                {!isLoadingDecisionMakers && !hasLoadedDecisionMakers && (
                  <Card
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "12px",
                      padding: "40px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      marginBottom: "20px",
                      minHeight: "400px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <PersonIcon
                      sx={{
                        fontSize: "80px",
                        color: "#D1D5DB",
                        marginBottom: "24px",
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      {source === "googlemaps"
                        ? "No Decision Makers Found"
                        : "No Decision Makers Yet"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {source === "googlemaps"
                        ? "Decision makers will appear here once companies are loaded"
                        : "Decision makers will appear here"}
                    </Typography>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Profile Details Modal */}
      <Dialog
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "672px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "56px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: { xs: "16px", sm: "24px" },
            paddingLeft: { xs: "16px", sm: "24px" },
            paddingTop: "16px",
            paddingBottom: "16px",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Arial",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0px",
              color: "#101828",
            }}
          >
            Profile Details
          </Typography>
          <IconButton
            onClick={() => setProfileModalOpen(false)}
            sx={{
              width: "24px",
              height: "24px",
              padding: 0,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <img
              src="/Button.svg"
              alt="Close"
              style={{ width: "24px", height: "24px" }}
            />
          </IconButton>
        </Box>

        <Box
          sx={{
            width: "100%",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#F5F5F5",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#D1D5DB",
              borderRadius: "4px",
              "&:hover": {
                background: "#9CA3AF",
              },
            },
          }}
        >
          {selectedDecisionMaker && (
            <>
              <Box
                sx={{
                  width: "100%",
                  minHeight: "87px",
                  display: "flex",
                  gap: "16px",
                }}
              >
                {selectedDecisionMaker.photo_url ? (
                  <Box
                    component="img"
                    src={selectedDecisionMaker.photo_url}
                    alt={
                      selectedDecisionMaker.name ||
                      selectedDecisionMaker.full_name
                    }
                    sx={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: "80px",
                      height: "80px",
                      bgcolor: "#D1D5DB",
                    }}
                  >
                    {(
                      selectedDecisionMaker.name ||
                      selectedDecisionMaker.full_name
                    )
                      ?.charAt(0)
                      ?.toUpperCase() || "U"}
                  </Avatar>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "18px",
                      lineHeight: "27px",
                      letterSpacing: "0px",
                      color: "#101828",
                      width: "100%",
                      height: "27px",
                    }}
                  >
                    {selectedDecisionMaker.name ||
                      selectedDecisionMaker.full_name ||
                      `${selectedDecisionMaker.first_name || ""} ${
                        selectedDecisionMaker.last_name || ""
                      }`.trim() ||
                      "Unknown"}
                  </Typography>

                  {selectedDecisionMaker.title && (
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#155DFC",
                        width: "528px",
                        height: "24px",
                      }}
                    >
                      {selectedDecisionMaker.title}
                    </Typography>
                  )}

                  {(selectedDecisionMaker.organization?.name ||
                    selectedDecisionMaker.organization_name) && (
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#4A5565",
                        width: "528px",
                        height: "24px",
                      }}
                    >
                      {selectedDecisionMaker.organization?.name ||
                        selectedDecisionMaker.organization_name}
                    </Typography>
                  )}

                  {selectedDecisionMaker.headline && (
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "0px",
                        color: "#6B7280",
                        width: "528px",
                        marginTop: "4px",
                      }}
                    >
                      {selectedDecisionMaker.headline}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Arial",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    color: "#101828",
                  }}
                >
                  Contact Information
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {selectedDecisionMaker.email && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <img
                        src="/IconEmail.svg"
                        alt="Email"
                        style={{ width: "16px", height: "16px" }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0px",
                          color: "#364153",
                        }}
                      >
                        {selectedDecisionMaker.email}
                      </Typography>
                    </Box>
                  )}

                  {(selectedDecisionMaker.formatted_address ||
                    (selectedDecisionMaker.city &&
                      selectedDecisionMaker.state) ||
                    selectedDecisionMaker.country) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <img
                        src="/IconLocation.svg"
                        alt="Location"
                        style={{ width: "16px", height: "16px" }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0px",
                          color: "#364153",
                        }}
                      >
                        {selectedDecisionMaker.formatted_address ||
                          [
                            selectedDecisionMaker.city,
                            selectedDecisionMaker.state,
                            selectedDecisionMaker.country,
                          ]
                            .filter(Boolean)
                            .join(", ") ||
                          selectedDecisionMaker.country ||
                          "Location not available"}
                      </Typography>
                    </Box>
                  )}

                  {selectedDecisionMaker.linkedin_url && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <img
                        src="/IconLinkedIn.svg"
                        alt="LinkedIn"
                        style={{ width: "16px", height: "16px" }}
                      />
                      <Link
                        href={selectedDecisionMaker.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0px",
                          color: "#155DFC",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {selectedDecisionMaker.linkedin_url}
                      </Link>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/IconExp.svg"
                    alt="Experience"
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#101828",
                    }}
                  >
                    Experience
                  </Typography>
                </Box>

                {selectedDecisionMaker.employment_history &&
                Array.isArray(selectedDecisionMaker.employment_history) &&
                selectedDecisionMaker.employment_history.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {selectedDecisionMaker.employment_history.map(
                      (job, index) => (
                        <Box
                          key={job.id || job._id || index}
                          sx={{
                            padding: "12px",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "8px",
                            border: "1px solid #E5E7EB",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Arial",
                              fontWeight: 600,
                              fontSize: "16px",
                              lineHeight: "24px",
                              color: "#101828",
                              mb: "4px",
                            }}
                          >
                            {job.title || "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Arial",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "20px",
                              color: "#4A5565",
                              mb: "4px",
                            }}
                          >
                            {job.organization_name || "Unknown Company"}
                          </Typography>
                          {job.start_date && (
                            <Typography
                              sx={{
                                fontFamily: "Arial",
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: "20px",
                                color: "#6B7280",
                              }}
                            >
                              {job.current ? "Current" : ""}{" "}
                              {job.start_date &&
                                `${new Date(job.start_date).toLocaleDateString(
                                  "en-US",
                                  { month: "short", year: "numeric" }
                                )}`}
                              {job.end_date &&
                                !job.current &&
                                ` - ${new Date(job.end_date).toLocaleDateString(
                                  "en-US",
                                  { month: "short", year: "numeric" }
                                )}`}
                            </Typography>
                          )}
                        </Box>
                      )
                    )}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#9CA3AF",
                    }}
                  >
                    No employment history available
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/IconSkills.svg"
                    alt="Skills"
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#101828",
                    }}
                  >
                    Skills
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  {(() => {
                    // Use functions, departments, or subdepartments from enriched_data
                    const functions = selectedDecisionMaker.functions || [];
                    const departments = selectedDecisionMaker.departments || [];
                    const subdepartments =
                      selectedDecisionMaker.subdepartments || [];
                    const skills =
                      selectedDecisionMaker.skills ||
                      selectedDecisionMaker.skill_list ||
                      selectedDecisionMaker.competencies ||
                      selectedDecisionMaker.expertise ||
                      selectedDecisionMaker.skills_list ||
                      selectedDecisionMaker.skills_array;

                    // Combine all available skill/function data
                    const allSkills = [
                      ...(Array.isArray(functions) ? functions : []),
                      ...(Array.isArray(departments) ? departments : []),
                      ...(Array.isArray(subdepartments) ? subdepartments : []),
                      ...(Array.isArray(skills) ? skills : []),
                    ];

                    // Remove duplicates and format
                    const uniqueSkills = [
                      ...new Set(
                        allSkills.map((s) =>
                          typeof s === "string"
                            ? s
                            : s.name || s.title || String(s)
                        )
                      ),
                    ].filter(Boolean);

                    if (uniqueSkills.length === 0) {
                      return (
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#9CA3AF",
                          }}
                        >
                          No skills listed
                        </Typography>
                      );
                    }

                    return uniqueSkills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        sx={{
                          backgroundColor: "#EFF6FF",
                          color: "#155DFC",
                          fontFamily: "Arial",
                          fontWeight: 400,
                          fontSize: "14px",
                          height: "32px",
                          borderRadius: "6px",
                          "& .MuiChip-label": {
                            padding: "0 12px",
                          },
                        }}
                      />
                    ));
                  })()}
                </Box>
              </Box>

              {/* Organization Details */}
              {selectedDecisionMaker.organization && (
                <Box
                  sx={{
                    width: "100%",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "10px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Arial",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0px",
                      color: "#101828",
                    }}
                  >
                    Organization Details
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {selectedDecisionMaker.organization.name && (
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#101828",
                            minWidth: "120px",
                          }}
                        >
                          Name:
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#364153",
                          }}
                        >
                          {selectedDecisionMaker.organization.name}
                        </Typography>
                      </Box>
                    )}

                    {selectedDecisionMaker.organization.website_url && (
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#101828",
                            minWidth: "120px",
                          }}
                        >
                          Website:
                        </Typography>
                        <Link
                          href={selectedDecisionMaker.organization.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#155DFC",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {selectedDecisionMaker.organization.website_url}
                        </Link>
                      </Box>
                    )}

                    {selectedDecisionMaker.organization.industry && (
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#101828",
                            minWidth: "120px",
                          }}
                        >
                          Industry:
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#364153",
                          }}
                        >
                          {selectedDecisionMaker.organization.industry}
                        </Typography>
                      </Box>
                    )}

                    {selectedDecisionMaker.organization
                      .estimated_num_employees && (
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#101828",
                            minWidth: "120px",
                          }}
                        >
                          Employees:
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#364153",
                          }}
                        >
                          {
                            selectedDecisionMaker.organization
                              .estimated_num_employees
                          }
                        </Typography>
                      </Box>
                    )}

                    {selectedDecisionMaker.seniority && (
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#101828",
                            minWidth: "120px",
                          }}
                        >
                          Seniority:
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Arial",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#364153",
                            textTransform: "capitalize",
                          }}
                        >
                          {selectedDecisionMaker.seniority}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {selectedDecisionMaker?.linkedin_url && (
                <Box
                  sx={{
                    width: "100%",
                    paddingTop: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <Button
                    onClick={() => {
                      if (selectedDecisionMaker.linkedin_url) {
                        window.open(
                          selectedDecisionMaker.linkedin_url,
                          "_blank"
                        );
                      }
                    }}
                    sx={{
                      width: "100%",
                      height: "50px",
                      borderRadius: "10px",
                      border: "1px solid #D1D5DC",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <img
                      src="/Iconwithbutton.svg"
                      alt="LinkedIn"
                      style={{ width: "20px", height: "20px" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Arial",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0px",
                        color: "#364153",
                        textAlign: "center",
                      }}
                    >
                      View LinkedIn
                    </Typography>
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog
        open={messageModalOpen}
        onClose={() => {
          setMessageModalOpen(false);
          setUserPrompt("");
          setAiMessage("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Send LinkedIn Message
          </Typography>
          <IconButton
            onClick={() => {
              setMessageModalOpen(false);
              setUserPrompt("");
              setAiMessage("");
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box
              sx={{
                backgroundColor: "#F9FAFB",
                padding: "12px",
                borderRadius: "8px",
              }}
            >
              <Typography variant="body2" sx={{ color: "#6B7280", mb: 1 }}>
                Sending to{" "}
                {selectedDecisionMakers.filter((dm) => dm.linkedin_url).length}{" "}
                decision maker(s)
              </Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                {selectedDecisionMakers
                  .filter((dm) => dm.linkedin_url)
                  .map((dm) => dm.full_name)
                  .join(", ")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Write your message prompt
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder="e.g., Write a professional LinkedIn message for a software engineer candidate with 5 years of experience in Python and React. Mention that we have a remote position with competitive salary."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleGenerateMessage}
                disabled={isGenerating || !userPrompt.trim()}
                sx={{
                  mt: 2,
                  backgroundColor: "#3B82F6",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                {isGenerating ? "Generating..." : "Generate AI Message"}
              </Button>
            </Box>

            {aiMessage && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    AI Generated Message
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRegenerateMessage}
                    disabled={isGenerating || !userPrompt.trim()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      borderColor: "#D1D5DB",
                      color: "#6B7280",
                      "&:hover": {
                        borderColor: "#9CA3AF",
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    Regenerate
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={aiMessage}
                  onChange={(e) => setAiMessage(e.target.value)}
                  placeholder="AI generated message will appear here..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#F9FAFB",
                      "& fieldset": {
                        borderColor: "#D1D5DB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#6B7280", mt: 1, display: "block" }}
                >
                  You can edit the message above before sending
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #E5E7EB",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              setMessageModalOpen(false);
              setUserPrompt("");
              setAiMessage("");
            }}
            sx={{ color: "#6B7280", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessages}
            disabled={!aiMessage.trim() || isSending}
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": {
                backgroundColor: "#2563EB",
              },
              "&:disabled": {
                backgroundColor: "#93C5FD",
              },
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            {isSending ? "Sending..." : "Send Messages"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyResults;
